import useSWR from "swr";
import type { WithPagination } from "../../../../types/types";
import type { AssetContainerType } from "../../../../util/assetContainer.types";
import {
  getAssetListEndpoint,
  getExternalLinksListEndpoint,
} from "../../../../util/assetEndpoints";
import { useStoreState } from "../../../../util/util";

type PaginatedTotal = WithPagination<{ data: unknown[] }>;

export function buildAssetsProbeUrl(
  containerType: AssetContainerType,
  tenantId: string,
  containerId: string,
  options?: { includeGeneratedAssets?: boolean }
): string {
  const base = getAssetListEndpoint(containerType, tenantId, containerId);
  const params = new URLSearchParams({ limit: "1", offset: "0" });
  if (containerType === "product") {
    params.set(
      "show_generated_assets",
      String(options?.includeGeneratedAssets ?? true)
    );
  }
  return `${base}?${params.toString()}`;
}

export function buildExternalLinksProbeUrl(
  containerType: AssetContainerType,
  tenantId: string,
  containerId: string
): string {
  const base = getExternalLinksListEndpoint(
    containerType,
    tenantId,
    containerId
  );
  return `${base}?limit=1&offset=0`;
}

export function useDocumentAvailability(
  containerType: AssetContainerType,
  containerId?: string
) {
  const { tenant_id } = useStoreState();
  const enabled = Boolean(tenant_id && containerId);

  const { data: assetsData, isValidating: isValidatingAssets } =
    useSWR<PaginatedTotal>(
      enabled
        ? buildAssetsProbeUrl(containerType, tenant_id, containerId!)
        : null,
      { revalidateOnFocus: false }
    );

  const { data: externalLinksData, isValidating: isValidatingExternalLinks } =
    useSWR<PaginatedTotal>(
      enabled
        ? buildExternalLinksProbeUrl(containerType, tenant_id, containerId!)
        : null,
      { revalidateOnFocus: false }
    );

  const hasAssets = (assetsData?.pagination?.total ?? 0) > 0;
  const hasExternalLinks = (externalLinksData?.pagination?.total ?? 0) > 0;
  const probesPending =
    assetsData === undefined || externalLinksData === undefined;

  return {
    hasAssetsOrExternalLinks: hasAssets || hasExternalLinks,
    hasAssets,
    hasExternalLinks,
    isLoading:
      enabled &&
      probesPending &&
      (isValidatingAssets || isValidatingExternalLinks),
  };
}

export function shouldShowEntityDocumentsTab({
  canModify,
  hasAssetsOrExternalLinks,
  isLoading,
}: {
  canModify: boolean;
  hasAssetsOrExternalLinks: boolean;
  isLoading: boolean;
}): boolean {
  if (canModify) {
    return true;
  }
  if (isLoading) {
    return false;
  }
  return hasAssetsOrExternalLinks;
}
